/**
 * Textarea with an inline highlight overlay.
 *
 * The actual <textarea> is rendered with transparent text fill (caret stays
 * visible) and is layered on top of a mirror <div> that paints the formatted
 * version of the same string. Because both elements share font, padding,
 * line-height and wrapping, the glyphs align pixel-perfectly so the user
 * sees formatting applied "live" inside the input.
 */

import { useEffect, useRef, type ChangeEvent, type CSSProperties } from "react";

import { cn } from "@/lib/cn";
import type { MessageEntity } from "@/api/templates";
import type { EmojiAsset } from "@/api/emoji";
import { renderSpans } from "./entities";

export interface HighlightedTextareaProps {
  value: string;
  entities: MessageEntity[];
  onChange: (e: ChangeEvent<HTMLTextAreaElement>) => void;
  onSelect?: () => void;
  placeholder?: string;
  rows?: number;
  textareaRef?: React.MutableRefObject<HTMLTextAreaElement | null>;
  className?: string;
  emojiAssets?: Record<string, EmojiAsset>;
}

export function HighlightedTextarea({
  value,
  entities,
  onChange,
  onSelect,
  placeholder,
  rows = 10,
  textareaRef,
  className,
  emojiAssets,
}: HighlightedTextareaProps) {
  const innerRef = useRef<HTMLTextAreaElement | null>(null);
  const overlayRef = useRef<HTMLDivElement | null>(null);

  // Sync scroll position so the overlay tracks the textarea as it scrolls.
  useEffect(() => {
    const ta = innerRef.current;
    const ov = overlayRef.current;
    if (!ta || !ov) return;
    const onScroll = () => {
      ov.scrollTop = ta.scrollTop;
      ov.scrollLeft = ta.scrollLeft;
    };
    ta.addEventListener("scroll", onScroll);
    return () => ta.removeEventListener("scroll", onScroll);
  }, []);

  const setRef = (el: HTMLTextAreaElement | null) => {
    innerRef.current = el;
    if (textareaRef) textareaRef.current = el;
  };

  const syncSelection = () => {
    onSelect?.();
  };

  const spans = renderSpans(value || " ", entities);

  return (
    <div
      className={cn(
        "relative rounded-2xl bg-surface border border-white/[0.06]",
        "focus-within:border-accent/40 transition-colors",
        className,
      )}
    >
      {/* Highlight mirror — sits BEHIND the textarea, paints the styled text. */}
      <div
        ref={overlayRef}
        aria-hidden
        className={cn(
          "absolute inset-0 p-4 overflow-auto pointer-events-none select-none",
          "text-[15px] leading-relaxed whitespace-pre-wrap break-words",
          "rounded-2xl",
        )}
      >
        {spans.map((s, i) => (
          <SpanView key={i} text={s.text} types={s.types} emojiAssets={emojiAssets} />
        ))}
        {/* Trailing newline to ensure the mirror has the same height as the textarea */}
        {"​"}
      </div>

      <textarea
        ref={setRef}
        value={value}
        onChange={onChange}
        onSelect={syncSelection}
        onKeyUp={syncSelection}
        onMouseUp={syncSelection}
        placeholder={placeholder}
        rows={rows}
        spellCheck={false}
        className={cn(
          "highlighted-textarea-input relative w-full p-4 resize-none bg-transparent",
          "text-[15px] leading-relaxed outline-none",
          "placeholder:text-text-faint",
        )}
        style={{
          // Text invisible (caret stays). All character glyphs — including
          // color emojis — are rendered by the mirror div instead.
          color: "transparent",
          WebkitTextFillColor: "transparent",
          caretColor: "white",
          minHeight: 200,
        }}
      />

      <div className="absolute bottom-2 right-3 text-[10px] text-text-faint tabular-nums">
        {value.length}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Inline span renderer — same as PreviewPanel but stricter about emoji handling.
// ---------------------------------------------------------------------------

function SpanView({
  text,
  types,
  emojiAssets,
}: {
  text: string;
  types: MessageEntity[];
  emojiAssets?: Record<string, EmojiAsset>;
}) {
  let node: React.ReactNode = text;

  const classes: string[] = [];
  let isBold = false;
  let isCode = false;
  let isLink = false;
  let isSpoiler = false;
  let isQuote = false;
  let emoji: MessageEntity | undefined;

  for (const t of types) {
    if (t.type === "bold") isBold = true;
    else if (t.type === "italic") classes.push("italic");
    else if (t.type === "underline") classes.push("underline");
    else if (t.type === "strikethrough") classes.push("line-through");
    else if (t.type === "code" || t.type === "pre") isCode = true;
    else if (t.type === "spoiler") isSpoiler = true;
    else if (t.type === "blockquote") isQuote = true;
    else if (t.type === "text_link" || t.type === "mention" || t.type === "text_mention") isLink = true;
    else if (t.type === "custom_emoji") emoji = t;
  }

  // The overlay must keep the same text metrics as the textarea under it.
  // Real font-weight/font-family/padding changes desync caret and line wraps.
  const metricSafeStyle: CSSProperties | undefined = isBold
    ? { textShadow: "0.35px 0 0 currentColor, -0.35px 0 0 currentColor" }
    : undefined;

  if (isCode) {
    return (
      <span
        className={cn("rounded bg-white/[0.08] text-text", classes.join(" "))}
        style={metricSafeStyle}
      >
        {text}
      </span>
    );
  }
  if (isLink) {
    return (
      <span className={cn("text-accent underline", classes.join(" "))} style={metricSafeStyle}>
        {text}
      </span>
    );
  }
  if (isSpoiler) {
    return (
      <span
        className={cn("rounded text-text", classes.join(" "))}
        style={{ background: "rgba(255,255,255,0.18)", ...metricSafeStyle }}
      >
        {text}
      </span>
    );
  }
  if (isQuote && !emoji) {
    return (
      <span
        className={cn(
          "editor-quote-span text-text",
          classes.join(" "),
        )}
        style={metricSafeStyle}
      >
        {text}
      </span>
    );
  }
  if (emoji) {
    const asset = emoji.custom_emoji_id
      ? emojiAssets?.[String(emoji.custom_emoji_id)]
      : undefined;
    return (
      <span
        className={cn("relative text-text", isQuote && "editor-quote-span", classes.join(" "))}
        style={metricSafeStyle}
        title={`premium emoji · id ${emoji.custom_emoji_id ?? "?"}`}
      >
        {asset?.image_url ? (
          <img
            src={asset.image_url}
            alt={text}
            className="inline-block size-[1.08em] align-[-0.12em] object-contain"
            loading="lazy"
          />
        ) : (
          text
        )}
        <span
          aria-hidden
          className="absolute -top-0.5 -right-1 size-1.5 rounded-full bg-accent shadow-[0_0_6px_rgba(255,107,53,0.8)]"
        />
      </span>
    );
  }

  node = (
    <span className={cn("text-text", classes.join(" "))} style={metricSafeStyle}>
      {text}
    </span>
  );
  return <>{node}</>;
}
